import React, { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import { useLoading } from '../../../commons/Loading';
import { useAuth } from '../../../commons/Auth';
import { deleteFile, getSignedUrl } from '../../../lib/fetch/files';
import { Col, CustomInput, FormGroup, Label, Row } from 'reactstrap';
import {
  BooleanField,
  DataTable,
  DateTimeField,
  Field,
} from '../../../commons/DataTable';
import SwitchField from './SwitchField';
import ActionsField from './ActionsField';
import AddFileButton from './AddFileButton';
import {
  ContFilterYear,
  ContPaginationPage,
  H5Styled,
} from '../../../styled-components';
import generatePaginationOptions from '../../../lib/helpers/generatePaginationOptions';
import DisponibilityField from './DisponibilityField';
import TypeField from './TypeField';
import axios from 'axios';
import {
  uploadCondoDocument,
  uploadCondoDocumentRASZip,
} from '../../../lib/fetch/condos';
import AddRASFileButton from './AddRASFileButton';

const CondoDocuments = ({ condo = {}, query, onFilter }) => {
  const [t] = useTranslation();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();
  const [{ newToken, profile = {}, newIsAdmin } = {}] = useAuth();

  const { limit, offset, active } = query;
  const [condoFiles, setCondoFiles] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [RASTypes, setRASTypes] = useState([]);
  const [total, setTotal] = useState(0);

  async function handleFileListView(key) {
    showLoading();

    const { data, error } = await getSignedUrl(key, newToken);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    window.open(data.url, '_blank');
  }

  async function getCondoFiles() {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/documents/${condo._id}/condo`,
      {
        headers: { Authorization: newToken },
        params: { limit, offset, active },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [], headers = {} } = response;

    hideLoading();
    setCondoFiles(data.documents);
    setTotal(+headers['x-total-count']);
    return data;
  }

  async function uploadRASZip(zipFiles, toggleAddNewFileModal) {
    showLoading();

    const { error, data } = await uploadCondoDocumentRASZip({
      id: condo.matchingAdmin?.id || '',
      data: zipFiles,
      newToken,
    });

    if (error) {
      hideLoading();

      toggleAddNewFileModal();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    toggleAddNewFileModal();
    getCondoFiles();
    alert.success(t('COMMON:SUCCESS_UPLOAD'));
  }

  async function uploadFile(file, toggleAddNewFileModal) {
    showLoading();

    const { error, data } = await uploadCondoDocument({
      id: condo.matchingAdmin?.id || '',
      data: file,
      newToken,
    });

    if (error) {
      hideLoading();

      toggleAddNewFileModal();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    toggleAddNewFileModal();
    getCondoFiles();
    alert.success(t('COMMON:SUCCESS_UPLOAD'));
  }

  async function handleDelete({ documentId }) {
    showLoading();

    const { error, data } = await deleteFile({
      documentId,
      newToken,
    });

    if (error) {
      hideLoading();

      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    getCondoFiles();
    hideLoading();
    alert.success(t('COMMON:SUCCESS_DELETE'));
  }

  const fetchDocumentTypes = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/document-types`,
      {
        headers: { Authorization: newToken },
        params: {
          limit: 9999,
          offset: 0,
          type:
            profile.type === 'CERTIFIER' ? 'CERTIFIER_CONDO' : 'ADMIN_CONDO',
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [] } = response;

    setDocumentTypes(data.documentTypes);
  };

  const fetchRASTypes = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/document-types`,
      {
        headers: { Authorization: newToken },
        params: {
          limit: 9999,
          offset: 0,
          type: 'RAS',
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [] } = response;

    setRASTypes(data.documentTypes);
  };

  useEffect(() => {
    if (condo._id) {
      getCondoFiles();
      fetchDocumentTypes();
      fetchRASTypes();
    }
  }, []);

  return (
    <>
      <Row
        xs={8}
        className="align-items-center"
        style={{ justifyContent: 'space-between' }}
      >
        <Col>
          <H5Styled>
            {t('ROYALTY:CONDO')}: {condo.name}
          </H5Styled>
        </Col>
      </Row>

      <Row className="align-items-center">
        <Col sm="12" md="4" className="select-wrapper">
          <ContFilterYear className="no-width">
            <Row>
              <Col xs="6">
                <Label>Stato documenti</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="active"
                    name="active"
                    value={active}
                    onChange={({ target: { value: active } }) =>
                      onFilter({ active })
                    }
                  >
                    <option value="" defaultChecked>
                      Tutti
                    </option>
                    <option value="ACTIVE">Attivi</option>
                    <option value="NOT_ACTIVE">Scaduti</option>
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
        </Col>
        <Col xs={8} style={{ marginBottom: 20, textAlign: 'end' }}>
          <AddRASFileButton
            uploadRASZip={uploadRASZip}
            profile={profile}
            RASTypes={RASTypes}
            condo={condo}
          />
          <AddFileButton
            uploadFile={uploadFile}
            profile={profile}
            documentTypes={documentTypes}
            RASTypes={RASTypes}
            condo={condo}
          />
        </Col>
      </Row>

      <DataTable
        data={condoFiles}
        total={total}
        offset={offset}
        limit={limit}
        onFilter={onFilter}
        emptyText={t('COMMON:NO_DATA')}
      >
        <Field title="Nome" source="file.name" className="text-left" />
        <TypeField title="Tipologia" source="type.name" className="text-left" />
        <DateTimeField
          title="Data Caricamento"
          source="file.createdAt"
          format="DD/MM/YYYY"
        />
        <DateTimeField
          title="Data Scadenza"
          source="expireDate"
          format="DD/MM/YYYY"
        />
        <Field title="Caricato da" source="uploadedBy" />
        <BooleanField title="Scaduto" source="expired" />
        <SwitchField
          title="Necessario per contratto"
          source="needed"
          condo={condo}
          disabled={newIsAdmin}
          getCondoFiles={getCondoFiles}
        />
        <DisponibilityField title="Disponibilità" />
        <ActionsField
          title="Azioni"
          handleFileListView={handleFileListView}
          deleteFileConfirm={handleDelete}
        />
      </DataTable>

      <ContPaginationPage>
        <p>{t('COMMON:PAGINATION_PER_PAGE')}</p>

        <FormGroup>
          <CustomInput
            type="select"
            className="pagination-select"
            id="perPage"
            name="perPage"
            value={query.limit}
            onChange={({ target: { value: limit } }) => {
              onFilter({ limit });
            }}
          >
            {generatePaginationOptions()}
          </CustomInput>
        </FormGroup>
      </ContPaginationPage>
    </>
  );
};

export default CondoDocuments;
